import React, { useState, useEffect, useRef } from "react";
import { getAgentResult } from "../../../api/const";
import { X, Bot } from "lucide-react";
import chatbotIcon from "../../../assets/images/mobile_chatbot.png";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Rnd } from "react-rnd";
import { useNavigate } from "react-router-dom"; // 라우팅 추가

// 로딩 애니메이션
const typingCSS = `
.typing span {
  display: inline-block;
  animation: blink 1.4s infinite both;
}
.typing span:nth-child(2) { animation-delay: 0.2s; }
.typing span:nth-child(3) { animation-delay: 0.4s; }

@keyframes blink {
  0% { opacity: .2; }
  20% { opacity: 1; }
  100% { opacity: .2; }
}
`;

const AgentChatbot = ({ onClose }) => {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate(); // Hook

  const loadingTexts = ["agent가 생각중 ...", "잠시만 기다려 주세요"];
  const [thinkingIndex, setThinkingIndex] = useState(0);
  const [showThinkingText, setShowThinkingText] = useState(false);

  const chatEndRef = useRef(null);

  // 자동 스크롤
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // AI 호출
  const handleAsk = async () => {
    if (!question.trim() || loading) return;

    const newMessages = [...messages, { role: "user", content: question }];
    setMessages(newMessages);
    setQuestion("");
    setLoading(true);

    try {
      // 이제 result는 { answer, routing, references, question } 형태
      const result = await getAgentResult({ question });

      const aiMsg = {
        role: "ai",
        content: result.answer,
        routing: result.routing,       // 라우팅 정보
        references: result.references  // 참조 정보
      };

      setMessages([...newMessages, aiMsg]);
    } catch (err) {
      console.error(err);
      setMessages([...newMessages, { role: "ai", content: "⚠️ 답변을 불러오지 못했습니다." }]);
    } finally {
      setLoading(false);
    }
  };

  // 로딩 문구 순환
  useEffect(() => {
    let delayTimer, rotateTimer;
    if (loading) {
      setShowThinkingText(false);
      setThinkingIndex(0);

      delayTimer = setTimeout(() => {
        setShowThinkingText(true);
        rotateTimer = setInterval(() => {
          setThinkingIndex((prev) => (prev + 1) % loadingTexts.length);
        }, 2000);
      }, 3000);
    }
    return () => {
      clearTimeout(delayTimer);
      clearInterval(rotateTimer);
    };
  }, [loading]);

  return (
    <>
      <style>{typingCSS}</style>
      <Rnd
        default={{
          x: window.innerWidth - 700,
          y: window.innerHeight - 600,
          width: 600,
          height: 500,
        }}
        minWidth={450}
        minHeight={400}
        bounds="window"
        dragHandleClassName="chatbot-drag"
        className="z-50"
      >
        <div className="bg-white border rounded-xl shadow-2xl flex flex-col overflow-hidden h-full">

          {/* 헤더 */}
          <div
            className="chatbot-drag flex items-center justify-between px-4 py-3 text-white"
            style={{ background: "linear-gradient(90deg, #1A1B2E 0%, #262742 100%)" }}
          >
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                <img src={chatbotIcon} alt="AI" className="w-4 h-4" />
              </div>
              <span className="font-semibold text-sm">P6ix AI</span>
            </div>

            {onClose && (
              <button onClick={onClose} className="hover:bg-blue-700/30 p-1 rounded-md">
                <X size={18} />
              </button>
            )}
          </div>

          {/* 대화 영역 */}
          <div className="p-4 flex-1 overflow-y-auto text-sm space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <p className="text-gray-400 text-center">질문을 입력해보세요.</p>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`px-3 py-2 rounded-lg shadow-sm max-w-[80%] ${msg.role === "user" ? "text-white" : "bg-white border text-gray-800"
                    }`}
                  style={msg.role === "user" ? { backgroundColor: "#1A1B2E" } : {}}
                >
                  {msg.role === "ai" && (
                    <div className="flex items-center mb-1 text-[#1A1B2E]">
                      <Bot size={14} className="mr-1" />
                      <span className="text-xs font-semibold">AI</span>
                    </div>
                  )}

                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      ul({ children, ...props }) {
                        return (
                          <ul className="list-disc list-inside pl-4" {...props}>
                            {children}
                          </ul>
                        );
                      },

                      li({ children, ...props }) {
                        return (
                          <li className="my-1" {...props}>
                            {children}
                          </li>
                        );
                      },

                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");

                        if (!inline && match) {
                          return (
                            <SyntaxHighlighter
                              {...props}
                              PreTag="div"
                              language={match[1]}
                              style={oneDark}
                            >
                              {String(children).replace(/\n$/, "")}
                            </SyntaxHighlighter>
                          );
                        }

                        return (
                          <code className="bg-gray-200 px-1 py-0.5 rounded" {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {String(msg.content)}
                  </ReactMarkdown>

                  {/* 라우팅 버튼 (RAG 결과) */}
                  {msg.routing && msg.routing.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.routing.map((route, rIdx) => (
                        <button
                          key={rIdx}
                          onClick={() => navigate(route.path)}
                          className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100 hover:bg-blue-100 transition-colors flex items-center"
                        >
                          <span className="mr-1">🔗</span>
                          {route.title}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 참조 법령 (RAG 결과) */}
                  {msg.references && msg.references.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-gray-100">
                      <p className="text-[11px] text-gray-500 font-semibold mb-1">참조 법령(상세보기)</p>
                      <div className="space-y-1">
                        {msg.references.map((ref, rIdx) => (
                          <details key={rIdx} className="group bg-gray-50 rounded border border-gray-200 open:bg-white open:shadow-sm transition-all">
                            <summary className="cursor-pointer p-2 text-[11px] font-bold text-blue-800 flex items-center justify-between select-none">
                              <span>[{ref.law_name} {ref.hierarchy}]</span>
                              <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                            </summary>
                            <div className="px-2 pb-2 text-[11px] text-gray-600 leading-relaxed whitespace-pre-wrap border-t border-gray-100 mt-1 pt-1">
                              {ref.text}
                            </div>
                          </details>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            ))}

            {/* 로딩 */}
            {loading && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-lg shadow-sm max-w-[80%] bg-white border text-gray-800">
                  <div className="flex items-center mb-1 text-blue-500">
                    <Bot size={14} className="mr-1" />
                    <span className="text-xs font-semibold">AI</span>
                  </div>

                  <div className="typing text-gray-500 text-xs">
                    <span>●</span><span>●</span><span>●</span>
                  </div>

                  {showThinkingText && (
                    <div className="mt-1 text-gray-400 text-[11px] italic">
                      {loadingTexts[thinkingIndex]}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* 입력창 */}
          <div className="flex border-t p-2 bg-white">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="질문 입력..."
              className="flex-1 border px-3 py-2 rounded-full text-sm focus:ring-2 focus:ring-[#1A1B2E] focus:outline-none"
              onKeyDown={(e) => e.key === "Enter" && handleAsk()}
            />

            <button
              onClick={handleAsk}
              disabled={!question.trim() || loading}
              className="ml-2 text-white px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "#1A1B2E" }}
            >
              전송
            </button>
          </div>
        </div>
      </Rnd>
    </>
  );
};

export default AgentChatbot;
